/**
 * VastuLifeAreasTable - Shows beneficial directions for each life area
 */

import React, { useState, useMemo } from 'react';
import { Box, Typography, Table, Sheet, Select, Option, Chip, Stack } from '@mui/joy';
import { PlanetName } from '../utils/astroEngine';
import { 
  getLifeAreaDirections, 
  VastuDirection, 
  LIFE_AREA_HOUSES,
  getPlanetDirection,
} from '../utils/vastuUtils';

interface VastuLifeAreasTableProps {
  placements: Record<string, number>;
  houseLords: Record<number, PlanetName>;
  houseNames: Record<number, string>;
  functionalBenefics?: Set<PlanetName>;
  exaltedPlanets?: Record<string, boolean>;
  shadbalaScores?: Partial<Record<PlanetName, number>>;
  blendedScores?: Partial<Record<PlanetName, number>>;
  transitPlacements?: Record<string, number>;
}

const DIRECTION_COLORS: Record<VastuDirection, string> = {
  'East': '#f59e0b',
  'Southeast': '#ec4899',
  'South': '#dc2626',
  'Southwest': '#6366f1',
  'West': '#8b5cf6',
  'Northwest': '#3b82f6',
  'North': '#10b981',
  'Northeast': '#f97316',
  'Center': '#6b7280',
};

const DIRECTION_REMEDIES: Record<VastuDirection, { remedy: string; symbol: string }> = {
  'East': { remedy: 'Ensure open windows for morning sunlight; gold/yellow décor; Surya Namaskar routine', symbol: '🌅' },
  'Southeast': { remedy: 'Keep kitchen or electrical panel here; rose quartz; pink & white tones', symbol: '🔥' },
  'South': { remedy: 'Place heavy furniture or safe here; red/brown accents; Hanuman or Mars yantra', symbol: '⚡' },
  'Southwest': { remedy: 'Store valuables/documents; black tourmaline; absolutely no water features or skylights', symbol: '🪸' },
  'West': { remedy: 'Ideal for study or dining; gray/steel décor items; Shani pooja on Saturdays', symbol: '💧' },
  'Northwest': { remedy: 'Good for guest room or storage; white/silver items; Chandra yantra; moonstone', symbol: '💨' },
  'North': { remedy: 'Keep open and clutter-free for prosperity; green plants; cash locker; Mercury yantra', symbol: '💰' },
  'Northeast': { remedy: 'Prayer/meditation corner; keep strictly clean & clutter-free; yellow turmeric; small water feature', symbol: '🕉️' },
  'Center': { remedy: 'Brahmasthan — keep empty/open; avoid heavy beams or pillars; place a white lamp', symbol: '🌌' },
};

type SortKey = 'area' | 'direction' | 'basis';

export default function VastuLifeAreasTable(props: VastuLifeAreasTableProps) {
  const {
    placements,
    houseLords,
    houseNames,
    functionalBenefics,
    exaltedPlanets,
    shadbalaScores,
    blendedScores,
    transitPlacements,
  } = props;

  const [sortBy, setSortBy] = useState<SortKey>('area');

  const lifeAreaDirections = getLifeAreaDirections(placements, houseLords, {
    functionalBenefics,
    exaltedPlanets,
    shadbalaScores,
    blendedScores,
  });

  const sortedEntries = useMemo(() => {
    const entries = Object.entries(lifeAreaDirections);
    if (sortBy === 'area') return entries.sort(([a], [b]) => a.localeCompare(b));
    if (sortBy === 'direction') return entries.sort(([, a], [, b]) => a.primaryDirection.localeCompare(b.primaryDirection));
    if (sortBy === 'basis') return entries.sort(([, a], [, b]) =>
      (a.occupantDirections.length > 0 ? 0 : 1) - (b.occupantDirections.length > 0 ? 0 : 1)
    );
    return entries;
  }, [lifeAreaDirections, sortBy]);

  // Current transit direction alerts (Saturn, Jupiter, Rahu)
  const transitAlerts = useMemo(() => {
    if (!transitPlacements) return [];
    const checks: Array<{ planet: PlanetName; color: string }> = [
      { planet: 'Saturn', color: '#6366f1' },
      { planet: 'Jupiter', color: '#f97316' },
      { planet: 'Rahu', color: '#8b5cf6' },
    ];
    return checks.flatMap(({ planet, color }) => {
      const house = (transitPlacements as Record<string, number>)[planet];
      if (!house) return [];
      const lord = houseLords[house];
      if (!lord) return [];
      const direction = getPlanetDirection(lord);
      const lifeArea = Object.entries(LIFE_AREA_HOUSES).find(([, hs]) => hs.includes(house))?.[0] ?? `H${house}`;
      return [{ planet, house, direction, lifeArea, color }];
    });
  }, [transitPlacements, houseLords]);

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography level="title-lg">
          Beneficial Directions for Life Areas
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Sort by:</Typography>
          <Select
            size="sm"
            value={sortBy}
            onChange={(_, val) => val && setSortBy(val as SortKey)}
            sx={{ minWidth: 160 }}
          >
            <Option value="area">Life Area (A–Z)</Option>
            <Option value="direction">Direction</Option>
            <Option value="basis">Basis (Occupant first)</Option>
          </Select>
        </Box>
      </Box>

      {/* Current transit alerts */}
      {transitAlerts.length > 0 && (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 1.5, mb: 2, bgcolor: 'background.level1' }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Current Transit Overlay</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {transitAlerts.map(({ planet, house, direction, lifeArea, color }) => (
              <Chip
                key={planet}
                size="sm"
                variant="soft"
                sx={{
                  bgcolor: `${color}15`,
                  border: `1px solid ${color}50`,
                  color,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                {planet} in H{house} ({lifeArea}) → {direction} zone
              </Chip>
            ))}
          </Stack>
          <Typography level="body-xs" sx={{ mt: 1, opacity: 0.7 }}>
            These planets currently transit natal houses that point to the above Vastu directions.
            Attend to those directional zones with appropriate remedies during this period.
          </Typography>
        </Sheet>
      )}

      <Sheet
        variant="outlined"
        sx={{
          borderRadius: 'md',
          overflow: 'auto',
          maxWidth: '100%',
        }}
      >
        <Table
          stickyHeader
          sx={{
            '& th': {
              bgcolor: 'background.level1',
              fontWeight: 600,
            },
            '& td': {
              py: 1,
            },
          }}
        >
          <thead>
            <tr>
              <th style={{ width: '20%' }}>Life Area</th>
              <th style={{ width: '10%' }}>Houses</th>
              <th style={{ width: '25%' }}>House Lords & Direction</th>
              <th style={{ width: '25%' }}>Occupants & Direction</th>
              <th style={{ width: '20%' }}>Beneficial Direction</th>
            </tr>
          </thead>
          <tbody>
            {sortedEntries.map(([area, data]) => {
              const dirColor = DIRECTION_COLORS[data.primaryDirection];
              const hasOccupants = data.occupantDirections.length > 0;

              return (
                <tr key={area}>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {area}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">
                      {data.houses.map(h => `H${h}`).join(', ')}
                    </Typography>
                  </td>
                  <td>
                    {data.lordDirections.map(({ house, lord, direction }) => (
                      <Box key={house} sx={{ mb: 0.5 }}>
                        <Typography level="body-xs">
                          <strong>H{house}</strong> ({houseNames[house] || ''}):{' '}
                          <span style={{ color: DIRECTION_COLORS[direction] }}>
                            {lord} → {direction}
                          </span>
                        </Typography>
                      </Box>
                    ))}
                  </td>
                  <td>
                    {data.occupantDirections.length > 0 ? (
                      data.occupantDirections.map(({ house, planet, direction }) => (
                        <Box key={house} sx={{ mb: 0.5 }}>
                          <Typography level="body-xs" sx={{ fontWeight: 500 }}>
                            <strong>H{house}</strong>:{' '}
                            <span style={{ color: DIRECTION_COLORS[direction] }}>
                              {planet} → {direction}
                            </span>
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography level="body-xs" sx={{ opacity: 0.5, fontStyle: 'italic' }}>
                        No occupants
                      </Typography>
                    )}
                  </td>
                  <td>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 'sm',
                        bgcolor: `${dirColor}20`,
                        border: `2px solid ${dirColor}`,
                      }}
                    >
                      <Typography
                        level="body-sm"
                        sx={{
                          fontWeight: 700,
                          color: dirColor,
                        }}
                      >
                        {data.primaryDirection}
                      </Typography>
                    </Box>
                    {hasOccupants && (
                      <Typography level="body-xs" sx={{ color: 'success.500', mt: 0.5 }}>
                        ★ Based on occupant
                      </Typography>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Sheet>

      <Sheet
        variant="soft"
        sx={{
          borderRadius: 'sm',
          p: 1.5,
          mt: 2,
          bgcolor: 'background.level1',
        }}
      >
        <Typography level="body-xs" sx={{ opacity: 0.8 }}>
          <strong>How to use:</strong> The beneficial direction is determined by the strongest planet
          influencing each life area. If a house has occupants, the best planet (functional benefic, 
          exalted, or highest shadbala/blended %) is selected. For empty houses, the house lord's 
          direction is used. Strengthen these directions in your home/office for better results in 
          each life area.
        </Typography>
      </Sheet>

      {/* Directional Remedies */}
      <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 2, mt: 2 }}>
        <Typography level="title-sm" sx={{ mb: 1.5 }}>Directional Remedies</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 1.5,
          }}
        >
          {(Object.entries(DIRECTION_REMEDIES) as [VastuDirection, { remedy: string; symbol: string }][])
            .filter(([dir]) => dir !== 'Center')
            .map(([dir, { remedy, symbol }]) => (
              <Box
                key={dir}
                sx={{
                  p: 1.5,
                  borderRadius: 'sm',
                  bgcolor: `${DIRECTION_COLORS[dir]}10`,
                  border: `1px solid ${DIRECTION_COLORS[dir]}40`,
                }}
              >
                <Typography level="body-xs" sx={{ fontWeight: 700, color: DIRECTION_COLORS[dir], mb: 0.5 }}>
                  {symbol} {dir}
                </Typography>
                <Typography level="body-xs">{remedy}</Typography>
              </Box>
            ))}
        </Box>
      </Sheet>
    </Box>
  );
}
